<template>
  <main class="container-fluid py-3">
    <h4 class="mb-3">
      <i class="fa fa-credit-card text-primary mr-2"></i>
      AI Agent — Tổng quan Billing
    </h4>
    <p class="text-muted">Theo dõi cross-client: phân bố mode, top tiêu, blocked, anomaly, health check.</p>

    <!-- Filter days -->
    <div class="mb-3">
      <b-form-radio-group v-model="days" :options="dayOptions" buttons button-variant="outline-primary" size="sm" @change="loadAll" />
      <b-button size="sm" variant="primary" class="ml-2" @click="loadAll"><i class="fa fa-refresh"></i> Tải lại</b-button>
      <b-button size="sm" variant="success" class="ml-2" @click="exportBilling"><i class="fa fa-download"></i> Export CSV kỳ này</b-button>
    </div>

    <!-- System total cards -->
    <div class="row mb-3" v-if="overview.system">
      <div class="col-md-3">
        <div class="card border-primary">
          <div class="card-body">
            <div class="text-muted small">Hội thoại</div>
            <h3>{{ (overview.system.conversations || 0).toLocaleString() }}</h3>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card border-info">
          <div class="card-body">
            <div class="text-muted small">Tổng token</div>
            <h3>{{ (overview.system.totalTokens || 0).toLocaleString() }}</h3>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card border-success">
          <div class="card-body">
            <div class="text-muted small">Doanh thu ước tính</div>
            <h3>{{ (overview.system.estimatedRevenueVnd || 0).toLocaleString() }} đ</h3>
            <small class="text-muted">
              Input {{ (overview.inputPricePer1M || 0).toLocaleString() }}đ/1M ·
              Output {{ (overview.outputPricePer1M || 0).toLocaleString() }}đ/1M
            </small>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card" :class="overview.system.blockedQuota > 0 ? 'border-danger' : 'border-secondary'">
          <div class="card-body">
            <div class="text-muted small">Bị chặn do hết quota</div>
            <h3 :class="overview.system.blockedQuota > 0 ? 'text-danger' : ''">{{ (overview.system.blockedQuota || 0).toLocaleString() }}</h3>
            <small class="text-muted">Lỗi: {{ overview.system.errorCount || 0 }}</small>
          </div>
        </div>
      </div>
    </div>

    <!-- Mode distribution -->
    <div class="row mb-3">
      <div class="col-md-6">
        <div class="card">
          <div class="card-header"><b>Phân bố chế độ thanh toán</b></div>
          <div class="card-body">
            <table class="table table-sm mb-0">
              <tr>
                <td><span class="badge badge-secondary">Chưa chọn</span></td>
                <td class="text-right">{{ (overview.modeDistribution || {}).none || 0 }} client</td>
              </tr>
              <tr>
                <td><span class="badge badge-info">Trả trước (pack)</span></td>
                <td class="text-right">{{ (overview.modeDistribution || {}).prepaid || 0 }} client</td>
              </tr>
              <tr>
                <td><span class="badge badge-success">Trả sau (postpaid)</span></td>
                <td class="text-right">{{ (overview.modeDistribution || {}).postpaid || 0 }} client</td>
              </tr>
            </table>
          </div>
        </div>
      </div>

      <!-- Health check -->
      <div class="col-md-6">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <b>Health check API keys</b>
            <b-button size="sm" variant="outline-primary" @click="loadHealth" :disabled="healthLoading">
              <i class="fa fa-refresh" :class="{ 'fa-spin': healthLoading }"></i> Kiểm tra
            </b-button>
          </div>
          <div class="card-body">
            <table class="table table-sm mb-0">
              <tr v-for="(v, k) in healthRows" :key="k">
                <td>{{ healthLabels[k] || k }}</td>
                <td class="text-right">
                  <span v-if="v && v.ok" class="text-success">
                    <i class="fa fa-check-circle"></i> OK
                    <small v-if="v.latencyMs" class="text-muted">({{ v.latencyMs }}ms)</small>
                  </span>
                  <span v-else class="text-danger">
                    <i class="fa fa-times-circle"></i> {{ (v && v.error) || 'Chưa test' }}
                  </span>
                </td>
              </tr>
              <tr v-if="health.errorRate1h">
                <td>Tỉ lệ lỗi 1 giờ qua</td>
                <td class="text-right">
                  <span :class="health.errorRate1h.rate >= 20 ? 'text-danger' : 'text-muted'">
                    {{ health.errorRate1h.rate }}% ({{ health.errorRate1h.error }}/{{ health.errorRate1h.total }})
                  </span>
                </td>
              </tr>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Anomaly -->
    <div class="card mb-3" v-if="anomalies.length">
      <div class="card-header bg-warning">
        <b><i class="fa fa-exclamation-triangle"></i> Anomaly — Client tiêu token bất thường hôm nay (> 3× median 7 ngày)</b>
      </div>
      <b-table small striped :items="anomalies" :fields="anomalyFields">
        <template #cell(clientName)="r">
          <a :href="`/ai-agent-logs?clientId=${r.item.clientId}`">{{ r.item.clientName || '(deleted)' }}</a>
          <small class="text-muted ml-2">{{ r.item.domain }}</small>
        </template>
        <template #cell(billingMode)="r">
          <span v-if="r.item.billingMode === 'postpaid'" class="badge badge-success">Trả sau</span>
          <span v-else-if="r.item.billingMode === 'prepaid'" class="badge badge-info">Trả trước</span>
          <span v-else class="badge badge-secondary">Chưa chọn</span>
        </template>
        <template #cell(ratio)="r">
          <b class="text-danger" v-if="r.item.ratio">{{ r.item.ratio }}×</b>
          <span v-else class="text-muted">N/A</span>
        </template>
      </b-table>
    </div>

    <!-- Top clients -->
    <div class="card mb-3">
      <div class="card-header"><b>Top 20 clients theo token usage ({{ days }} ngày)</b></div>
      <b-table small striped hover :items="overview.topClients || []" :fields="clientFields">
        <template #cell(name)="r">
          <a :href="`/ai-agent-logs?clientId=${r.item._id}`">{{ r.item.name || '(deleted)' }}</a>
          <small class="text-muted ml-2">{{ r.item.domain }}</small>
          <span v-if="r.item.hasRisk" class="badge badge-danger ml-2"><i class="fa fa-warning"></i> Risk</span>
        </template>
        <template #cell(billingMode)="r">
          <span v-if="r.item.billingMode === 'postpaid'" class="badge badge-success">Trả sau</span>
          <span v-else-if="r.item.billingMode === 'prepaid'" class="badge badge-info">Trả trước</span>
          <span v-else class="badge badge-secondary">Chưa chọn</span>
        </template>
        <template #cell(totalTokens)="r">{{ (r.item.totalTokens || 0).toLocaleString() }}</template>
        <template #cell(estimatedCostVnd)="r"><b>{{ (r.item.estimatedCostVnd || 0).toLocaleString() }} đ</b></template>
        <template #cell(errorRate)="r">
          <span :class="r.item.errorRate >= 20 ? 'text-danger' : (r.item.errorRate >= 10 ? 'text-warning' : 'text-muted')">
            {{ r.item.errorRate }}%
          </span>
        </template>
        <template #cell(blockedQuota)="r">
          <span v-if="r.item.blockedQuota > 0" class="badge badge-danger">{{ r.item.blockedQuota }}</span>
          <span v-else class="text-muted">0</span>
        </template>
      </b-table>
    </div>

    <!-- Blocked recent -->
    <div class="card mb-3" v-if="(overview.blockedRecent || []).length">
      <div class="card-header bg-light"><b>Client bị chặn do hết quota (gần nhất 30)</b></div>
      <b-table small striped :items="overview.blockedRecent" :fields="blockedFields">
        <template #cell(name)="r">
          <a :href="`/ai-agent-logs?clientId=${r.item._id}`">{{ r.item.name || '(deleted)' }}</a>
          <small class="text-muted ml-2">{{ r.item.domain }}</small>
        </template>
        <template #cell(billingMode)="r">
          <span v-if="r.item.billingMode === 'postpaid'" class="badge badge-success">Trả sau</span>
          <span v-else-if="r.item.billingMode === 'prepaid'" class="badge badge-info">Trả trước</span>
          <span v-else class="badge badge-secondary">Chưa chọn</span>
        </template>
        <template #cell(lastBlock)="r">{{ formatTime(r.item.lastBlock) }}</template>
        <template #cell(creditBalance)="r">{{ (r.item.creditBalance || 0).toLocaleString() }} đ</template>
      </b-table>
    </div>
  </main>
</template>

<script>
export default {
  data() {
    return {
      days: 30,
      dayOptions: [
        { text: '7 ngày', value: 7 },
        { text: '30 ngày', value: 30 },
        { text: '90 ngày', value: 90 },
      ],
      overview: {},
      anomalies: [],
      health: {},
      healthLoading: false,
      healthLabels: {
        openai: 'OpenAI (GPT-4o)',
        customEmbed: 'Custom Embed (bgem3)',
        fptCloud: 'FPT Cloud',
        huggingFace: 'HuggingFace',
      },
      clientFields: [
        { key: 'name', label: 'Client' },
        { key: 'billingMode', label: 'Chế độ', class: 'text-center' },
        { key: 'conversations', label: 'Hội thoại', class: 'text-right' },
        { key: 'totalTokens', label: 'Tokens', class: 'text-right' },
        { key: 'estimatedCostVnd', label: 'Doanh thu ước tính', class: 'text-right' },
        { key: 'errorRate', label: '% lỗi', class: 'text-right' },
        { key: 'blockedQuota', label: 'Blocked', class: 'text-center' },
      ],
      anomalyFields: [
        { key: 'clientName', label: 'Client' },
        { key: 'billingMode', label: 'Chế độ', class: 'text-center' },
        { key: 'todayTokens', label: 'Token hôm nay', class: 'text-right', formatter: v => (v || 0).toLocaleString() },
        { key: 'medianTokens7d', label: 'Median 7 ngày', class: 'text-right', formatter: v => (v || 0).toLocaleString() },
        { key: 'ratio', label: 'Bội số', class: 'text-center' },
        { key: 'todayCount', label: 'Hội thoại', class: 'text-right' },
      ],
      blockedFields: [
        { key: 'name', label: 'Client' },
        { key: 'billingMode', label: 'Chế độ', class: 'text-center' },
        { key: 'count', label: 'Số lần block', class: 'text-right' },
        { key: 'creditBalance', label: 'Số dư', class: 'text-right' },
        { key: 'lastBlock', label: 'Block gần nhất', class: 'text-right' },
      ],
    };
  },
  computed: {
    healthRows() {
      const { openai, customEmbed, fptCloud, huggingFace } = this.health;
      return { openai, customEmbed, fptCloud, huggingFace };
    },
  },
  methods: {
    loadAll() {
      this.loadOverview();
      this.loadAnomaly();
    },
    loadOverview() {
      this.$service.get('/ai-agent/billing-overview', { params: { days: this.days } })
        .then(r => { this.overview = r.data; })
        .catch(e => console.error('Load overview fail', e));
    },
    loadAnomaly() {
      this.$service.get('/ai-agent/anomaly')
        .then(r => { this.anomalies = (r.data && r.data.anomalies) || []; })
        .catch(e => console.error('Load anomaly fail', e));
    },
    loadHealth() {
      this.healthLoading = true;
      this.$service.get('/ai-agent/openai-health')
        .then(r => { this.health = r.data; })
        .catch(e => console.error('Load health fail', e))
        .finally(() => { this.healthLoading = false; });
    },
    exportBilling() {
      const period = new Date().toISOString().slice(0, 7);
      this.$service.get('/ai-agent/export-billing', { params: { period }, responseType: 'blob' })
        .then(res => {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const a = document.createElement('a');
          a.href = url;
          a.download = `ai-agent-billing-${period}.csv`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        })
        .catch(e => console.error('Export fail', e));
    },
    formatTime(ts) {
      if (!ts) return '';
      return new Date(ts).toLocaleString('vi-VN');
    },
  },
  mounted() {
    this.loadAll();
    this.loadHealth();
  },
};
</script>
